"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const galleryImages = [
{
  src: "https://images.unsplash.com/photo-1717500251880-e8209ae23fa8",
  alt: "Sala principal de Pilates com reformers modernos e iluminação natural abundante",
  caption: "Sala de Pilates — Reformers",
  size: "large"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c64828ed-1772741693625.png",
  alt: "Sala de aulas de circuito funcional com equipamentos de última geração",
  caption: "Circuito Funcional",
  size: "small"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc5bf32c-1772741695524.png",
  alt: "Aula em andamento com instrutora orientando aluna em exercício de equilíbrio",
  caption: "Aula em Andamento",
  size: "small"
},
{
  src: "https://images.unsplash.com/photo-1672985023221-b884e8d11ba0",
  alt: "Recepção acolhedora com design moderno em tons neutros e plantas naturais",
  caption: "Recepção Acolhedora",
  size: "medium"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf9f9b52-1772741695470.png",
  alt: "Detalhe dos equipamentos de Pilates — Cadillac e Chair em ambiente iluminado",
  caption: "Equipamentos Premium",
  size: "medium"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1108e0c59-1772183047749.png",
  alt: "Sala de terapias com mesa de massagem e iluminação suave e relaxante",
  caption: "Sala de Terapias",
  size: "small"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1647078ab-1772741696573.png",
  alt: "Vista geral do estúdio com pé direito alto e janelas amplas",
  caption: "Vista Geral do Estúdio",
  size: "small"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f64d2570-1772741696060.png",
  alt: "Espaço de relaxamento com plantas e elementos naturais de design biofílico",
  caption: "Área de Relaxamento",
  size: "large"
}];


export default function GalleryGrid() {
  const ref = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<{src: string;alt: string;} | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.05 }
    );
    ref.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <>
      <section className="py-28 px-6 md:px-10 bg-cream-dark" ref={ref}>
        <div className="max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <span className="reveal-hidden text-xs uppercase tracking-widest text-gold font-semibold mb-4 block">
              Galeria
            </span>
            <h2 className="reveal-hidden reveal-delay-1 font-serif text-4xl md:text-5xl text-forest leading-tight">
              Conheça nossos{" "}
              <span className="italic font-light text-sage">espaços</span>
            </h2>
            <p className="reveal-hidden reveal-delay-2 text-stone mt-4 max-w-xl mx-auto">
              Ambientes projetados para oferecer o melhor em conforto, segurança e bem-estar.
            </p>
          </div>

          {/* Masonry-style grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px]">
            {galleryImages.map((img, i) =>
            <div
              key={`gallery-${i}`}
              onClick={() => setLightbox({ src: img.src, alt: img.alt })}
              className={`gallery-item reveal-hidden reveal-delay-${i % 4 + 1} cursor-pointer relative group
                  ${img.size === "large" ? "col-span-2 row-span-2" : ""}
                  ${img.size === "medium" ? "col-span-2 row-span-1" : ""}
                  ${img.size === "small" ? "col-span-1 row-span-1" : ""}
                `}>
              
                <AppImage
                src={img.src}
                alt={img.alt}
                fill
                className="object-cover w-full h-full" />
              
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/40 transition-all duration-400 flex items-end p-4">
                  <p className="text-cream text-xs font-medium translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {img.caption}
                  </p>
                </div>
                {/* Expand icon */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-charcoal">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox &&
      <div
        className="fixed inset-0 z-50 bg-charcoal/90 flex items-center justify-center p-4 cursor-pointer"
        onClick={() => setLightbox(null)}>
        
          <div
          className="relative max-w-4xl w-full max-h-[85vh] rounded-3xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}>
          
            <AppImage
            src={lightbox.src}
            alt={lightbox.alt}
            width={1200}
            height={800}
            className="w-full h-full object-contain" />
          
            <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors"
            aria-label="Fechar">
            
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
    </>);

}